import { type FC, type ChangeEvent, useRef, useEffect, useState } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'
import { calculateTooltipPosition, getMarkClass } from './utils'

export enum RangeSliderVariantV2 {
  Default = 'default',
  Split = 'split',
}
interface RangeSliderProps extends DefaultProps {
  variant?: RangeSliderVariantV2
  fillFrom: number
  fillTo: number
  value: number
  label?: string
  recommendedLabel?: string
  recommendedValue?: number
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  max?: number
  showValue?: boolean
  showOriginalArrow?: boolean
  showRecommededArrow?: boolean
  isShowRecommendedLabel?: boolean
}

const TOTAL_CELLS = 25
const GROUP_CELLS = 5

export const RangeSliderV2: FC<RangeSliderProps> = ({
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
  fillFrom,
  fillTo,
  label,
  value,
  recommendedLabel,
  recommendedValue,
  onChange,
  variant = RangeSliderVariantV2.Default,
  max = 100,
  showValue = true,
  showRecommededArrow = false,
  showOriginalArrow = false,
  isShowRecommendedLabel = false,
}) => {
  const uniqueId = id ?? `range-slider-${Math.random().toString(36).substr(2, 9)}`

  const adjustedValue = Math.max(0, Math.min(value, max))

  const [cellSize, setCellSize] = useState<number>(0)
  const cellRef = useRef<HTMLDivElement | null>(null)
  const initialValueRef = useRef(adjustedValue)
  const recommendedValueRef = useRef(recommendedValue ?? adjustedValue)

  const calculateCellSize = (): void => {
    if (cellRef.current) {
      const width = cellRef.current.getBoundingClientRect().width
      setCellSize(width)
    }
  }

  useEffect(() => {
    calculateCellSize()

    const handleResize = (): void => {
      calculateCellSize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const baseClass = cx('flex flex-col items-center gap-2 w-full')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  const tooltipPosition = calculateTooltipPosition(initialValueRef.current, cellSize)
  const recommendedTooltipPosition = calculateTooltipPosition(recommendedValueRef.current, cellSize)

  const calculateTrackFill = (): number => adjustedValue * cellSize - 10

  const thumbSize = `${(cellSize + 20).toString()}px`

  const thumbStyle = {
    left: `${calculateTrackFill().toString()}px`,
    width: thumbSize,
    height: thumbSize,
    backgroundColor: adjustedValue >= fillFrom && adjustedValue <= fillTo ? '#37A7FD' : 'white',
    boxShadow: '1px 2px 8px rgba(0, 0, 0, 0.3)',
  }

  if (variant === RangeSliderVariantV2.Split) {
    return (
      <div
        className={finalClasses}
        id={uniqueId}
        data-testid="range-slider-container"
        style={{
          visibility: cellSize > 0 ? 'visible' : 'hidden',
        }}
      >
        <div className="relative flex flex-col items-center gap-2 w-full max-w-[1600px]">
          {label ? (
            <div
              className="absolute text-[#3b3e3c] border border-[#3b3e3c] transform -translate-x-1/2 -translate-y-1/2 text-xs md:text-sm px-3 py-1 rounded-full"
              style={{
                left: tooltipPosition,
                top: `-${((cellSize + 30) / 2).toString()}px`,
              }}
            >
              {label}
              <div className="absolute z-10 top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-[#3b3e3c]" />
            </div>
          ) : null}
          <div className="relative w-full h-10 min-w-40">
            <input
              type="range"
              min="0"
              max={TOTAL_CELLS - 1}
              step="1"
              value={adjustedValue}
              className="absolute top-0 left-0 w-full appearance-none h-10 bg-transparent accent-white z-[4] opacity-0 cursor-pointer"
              onChange={onChange}
              {...(testId ? { 'data-testid': testId } : {})}
              {...(id ? { id } : {})}
              {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
              aria-hidden="true"
            />
            <div
              className="absolute cursor-pointer top-1/2 z-[2] border border-white transform -translate-y-1/2 bg-[#35A8FD] rounded-full"
              style={thumbStyle}
            />

            <div className="absolute top-1/2 left-0 w-full flex justify-between transform -translate-y-1/2 z-0">
              {[...Array<number>(Math.ceil(TOTAL_CELLS / GROUP_CELLS))].map((item, groupIndex) => (
                <div
                  key={groupIndex}
                  className="flex w-full h-full py-1 md:py-2 first:shadow-none shadow-[inset_2px_0px_0px_0px_#0190FE]"
                  style={{
                    visibility: cellSize ? 'visible' : 'hidden',
                  }}
                >
                  {[...Array<number>(GROUP_CELLS)].map((_, cellIndex) => {
                    const index = groupIndex * GROUP_CELLS + cellIndex
                    return (
                      <div
                        key={index}
                        ref={cellRef}
                        className={cx(
                          `w-full max-h-2 md:max-h-3 bg-grey-50 ${getMarkClass(index, fillFrom, fillTo)} z-[1]`,
                          {
                            'shadow-[inset_0_5px_5px_-3px_rgba(0,0,0,0.10),inset_0_-4px_4px_-3px_rgba(0,0,0,0.10)]':
                              index === 0,
                            'shadow-[inset_0_5px_5px_-3px_rgba(0,0,0,0.10),inset_0_-4px_4px_-3px_rgba(0,0,0,0.10),inset_1px_0px_0px_0px_#0190FE]':
                              index !== 0 && index % 5 !== 0,
                            'shadow-[inset_0_5px_5px_-3px_rgba(0,0,0,0.10),inset_0_-4px_4px_-3px_rgba(0,0,0,0.10),inset_2px_0px_0px_0px_#0190FE]':
                              index % 5 === 0 && index !== 0,
                          },
                        )}
                        style={{
                          height: cellSize ? `${cellSize.toString()}px` : 'auto',
                        }}
                      />
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
          {recommendedLabel && recommendedTooltipPosition && cellSize ? (
            <div
              className="absolute"
              style={{
                left: recommendedTooltipPosition,
                bottom: `-${(cellSize / 2).toString()}px`,
              }}
            >
              <div className="relative z-[10] transform -translate-x-1/2">
                <div className="w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-[#37A7FD]" />
              </div>
              {isShowRecommendedLabel ? (
                <div className="absolute z-[2] text-[#37A7FD] border border-[#37A7FD] transform -translate-x-1/2 text-xs md:text-sm px-3 py-1 rounded-full">
                  {recommendedLabel}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div
      className={finalClasses}
      id={uniqueId}
      data-testid="range-slider-container"
      style={{
        visibility: cellSize > 0 ? 'visible' : 'hidden',
      }}
    >
      <div className={cx('flex justify-between w-full')}>
        {label ? <span className="text-lg font-light">{label}</span> : null}
        {showValue ? <span className="text-lg font-semibold">{adjustedValue}%</span> : null}
      </div>
      <div className="relative flex flex-col items-center gap-2 w-full max-w-[1600px]">
        {showOriginalArrow ? (
          <div className="h-2 relative w-full -mb-1 md:mb-0 lg:mb-1">
            <div
              data-testid="range-slider-original-arrow"
              className="absolute left-1/2 transform -translate-x-1/2 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-meta"
              style={{
                left: tooltipPosition,
              }}
            />
          </div>
        ) : null}
        <div className="relative w-full h-8 min-w-72">
          <input
            type="range"
            min="0"
            max={max}
            step="1"
            value={adjustedValue}
            className="absolute top-0 left-0 w-full appearance-none h-8 bg-transparent accent-white z-[4] opacity-0 cursor-pointer"
            onChange={onChange}
            {...(testId ? { 'data-testid': testId } : {})}
            {...(id ? { id } : {})}
            {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
            aria-hidden="true"
          />
          <div
            className="absolute cursor-pointer top-1/2 z-[2] border border-white transform -translate-y-1/2 bg-[#35A8FD] rounded-full"
            style={thumbStyle}
          />

          <div className="absolute top-1/2 left-0 w-full flex justify-between transform -translate-y-1/2 z-0">
            {[...Array<number>(max + 1)].map((_, index) => {
              return (
                <div
                  key={index}
                  ref={cellRef}
                  className={cx(`w-full max-h-2 md:max-h-3 bg-grey-50 ${getMarkClass(index, fillFrom, fillTo)} z-[1]`, {
                    'shadow-[inset_0_5px_5px_-3px_rgba(0,0,0,0.10),inset_0_-3px_3px_-3px_rgba(0,0,0,0.10)]':
                      index === 0,
                    'shadow-[inset_0_5px_5px_-3px_rgba(0,0,0,0.10),inset_0_-3px_3px_-3px_rgba(0,0,0,0.10),inset_1.5px_0px_0px_0px_#0190FE]':
                      index !== 0,
                  })}
                  style={{
                    height: cellSize ? `${cellSize.toString()}px` : 'auto',
                  }}
                />
              )
            })}
          </div>
        </div>
        {showRecommededArrow && recommendedTooltipPosition ? (
          <div className="h-2 relative w-full -mt-1 md:mt-0 lg:mt-1">
            <div
              data-testid="range-slider-recommended-arrow"
              className="absolute z-[2] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-[#4AA8F9]"
              style={{
                left: recommendedTooltipPosition,
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
